import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getDocument } from '../../services/content';
import PastPapers from './PastPapers';
import VideoLessons from './VideoLessons';
import ExamList from '../exam/ExamList';

const SubjectDetail = () => {
  const { subjectId } = useParams();
  const { getUserLevel } = useAuth();
  const [subject, setSubject] = useState(null);
  const [activeTab, setActiveTab] = useState('papers');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSubject = async () => {
      try {
        const subjectData = await getDocument('subjects', subjectId);
        
        // Verify user has access to this subject's level
        const userLevel = getUserLevel();
        if (userLevel !== 'all' && subjectData.levelId !== userLevel) {
          setError('You do not have access to this subject');
          return;
        }
        
        setSubject(subjectData);
      } catch (err) {
        setError('Subject not found or access denied');
      } finally {
        setLoading(false);
      }
    };

    if (subjectId) {
      fetchSubject();
    }
  }, [subjectId, getUserLevel]);

  if (loading) {
    return (
      <div className="subject-detail">
        <div className="loading-container">
          <div className="loading-spinner">Loading subject...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subject-detail">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!subject) {
    return (
      <div className="subject-detail">
        <div className="error-message">Subject not found</div>
      </div>
    );
  }

  const tabs = [
    { id: 'papers', label: 'Past Papers', icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14,2 14,8 20,8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
        <polyline points="10,9 9,9 8,9"/>
      </svg>
    )},
    { id: 'videos', label: 'Video Lessons', icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="23 7 16 12 23 17 23 7"/>
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
      </svg>
    )},
    { id: 'exams', label: 'Practice Exams', icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    )}
  ];

  return (
    <div className="subject-detail">
      <div className="subject-header">
        <Link to="/dashboard" className="back-link">
          ← Back to Dashboard
        </Link>
        
        <div className="subject-info">
          <div className="subject-details">
            <h1>{subject.name}</h1>
            <p>{subject.description}</p>
          </div>
        </div>
      </div>

      <div className="subject-tabs">
        <div className="tab-navigation">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="tab-content">
          {activeTab === 'papers' && <PastPapers subjectId={subjectId} />}
          {activeTab === 'videos' && <VideoLessons subjectId={subjectId} />}
          {activeTab === 'exams' && <ExamList subjectId={subjectId} />}
        </div>
      </div>
    </div>
  );
};

export default SubjectDetail;
